import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Spinner, Typography } from '@scale/scaleui';
import { CodeAnnotation, CodeField } from '@scale/shared/types/params/textcollection';
import { CodeFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { CodeExecutionStatus } from '@scale/shared/textcollection/defaults';
import uuid from 'uuid';
import clsx from 'clsx';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface CodeFieldProps {
  field: CodeField;
  isCustomer?: boolean; // to control hidden test cases
  annotation: CodeFieldAnnotation;
  setAnnotation: (annotation: CodeFieldAnnotation) => void;
  disabled?: boolean;
  setSubmitError?: (field_id: string, error: string) => void;
  projectId?: string;
}

// features not supported:
// - adding test cases
// - uploading files
function CodeFieldSectionSimple({
  field,
  annotation,
  setAnnotation,
  disabled,
  setSubmitError: _setSubmitError,
  projectId,
}: CodeFieldProps) {
  const sphereWidgetId = useRef(uuid.v4());
  const initialAnnotation = useRef<CodeFieldAnnotation>({
    ...annotation,
    response: [
      {
        sourceCode: annotation?.response?.[0]?.sourceCode ?? '',
        language: field.languages[0],
        testCases: field.test_cases ?? [],
      },
    ],
  });

  const dataWidget = 'b052cff4e6afa0ac34cda19aa0f34ffd';

  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const [codeDirty, setCodeDirty] = useState(false);
  const [codeRanSuccessfully, setCodeRunsSuccessfully] = useState(false);
  const [listenerAdded, setListenerAdded] = useState(false);
  const ignoreAllCodeValidation = useUnleashFeatureFlag(
    'ignore-all-code-validation',
    { projectId },
    false,
  );

  const canSubmitMessage = useMemo(() => {
    if (ignoreAllCodeValidation) {
      return '';
    }
    if (!isWidgetLoaded || (!listenerAdded && !disabled)) {
      return `Please wait for code widget to load`;
    } else if (field.can_add_test_cases && !annotation.response?.[0]?.testCases?.length) {
      return `Please add at least one test case to ${field.title}`;
    } else if (codeDirty || !codeRanSuccessfully) {
      return `Please ensure the code in ${field.title} compiles and executes successfully`;
    } else {
      return '';
    }
  }, [
    codeDirty,
    codeRanSuccessfully,
    field,
    isWidgetLoaded,
    annotation.response,
    listenerAdded,
    disabled,
    ignoreAllCodeValidation,
  ]);

  const setSubmitError = useCallback(
    (error: string) => {
      _setSubmitError && _setSubmitError(field.field_id, error);
    },
    [_setSubmitError, field.field_id],
  );

  useEffect(() => {
    if (!setSubmitError) {
      return;
    }
    setSubmitError(canSubmitMessage);
  }, [canSubmitMessage, setSubmitError]);

  const updateAnnotation = useCallback(
    (data: any) => {
      setCodeDirty(true);
      const newSourceCode: string = data.source;
      setAnnotation({
        ...initialAnnotation.current,
        response: [
          {
            ...initialAnnotation.current.response[0],
            sourceCode: newSourceCode,
          },
        ],
      });
    },
    [setAnnotation],
  );

  const updateCanSubmit = useCallback((data: any) => {
    setCodeRunsSuccessfully(data.statusNumber === CodeExecutionStatus.Success);
    setCodeDirty(false);
  }, []);

  // Return true if the widget is initialized correctly
  const loadSphereWidget = useCallback(
    (initialAnnotation: CodeAnnotation) => {
      // @ts-ignore
      const maybeWidget = window.SEC?.widget?.(sphereWidgetId.current);
      if (maybeWidget) {
        maybeWidget.config({
          code: {
            compiler: initialAnnotation.language,
            source: initialAnnotation.sourceCode?.length ? initialAnnotation.sourceCode : undefined,
            input: field.default_input ?? initialAnnotation.testCases?.[0]?.input,
          },
          compilers: {
            list: field.languages,
          },
        });
        return true;
      }
      return false;
    },
    [field.default_input, field.languages],
  );

  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'sphere-engine-compilers-initialization-script';
    script.innerHTML = `SEC_HTTPS = true;
SEC_BASE = "compilers.widgets.sphere-engine.com"; 
(function(d, s, id){ SEC = window.SEC || (window.SEC = []);
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return; js = d.createElement(s); js.id = id; 
    js.src = (SEC_HTTPS ? "https" : "http") + "://" + SEC_BASE + "/static/sdk/sdk.js";
    fjs.parentNode.insertBefore(js, fjs);   
}(document, "script", "sphere-engine-compilers-jssdk"));`;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isWidgetLoaded) {
        if (
          loadSphereWidget({
            sourceCode: initialAnnotation.current?.response?.[0]?.sourceCode ?? '',
            language: initialAnnotation.current?.response?.[0]?.language,
            testCases: [],
          })
        ) {
          setIsWidgetLoaded(true);
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => {
      clearInterval(timer);
    };
  }, [loadSphereWidget, isWidgetLoaded]);

  useEffect(() => {
    // @ts-ignore
    const maybeWidget = window.SEC?.widget?.(sphereWidgetId.current);
    if (!disabled && isWidgetLoaded) {
      maybeWidget.events.subscribe('sourceChanged', updateAnnotation);
      maybeWidget.events.subscribe('checkStatus', updateCanSubmit);
      setListenerAdded(true);
    }
  }, [disabled, isWidgetLoaded, updateAnnotation, updateCanSubmit]);

  return (
    <div className="scale-tailwind scaleui scale-theme">
      {disabled && (
        <Typography.Caption>Read only: Your response won't be saved.</Typography.Caption>
      )}
      <div className={clsx({ hidden: !isWidgetLoaded })}>
        <div
          className={'sec-widget'}
          data-id={sphereWidgetId.current}
          data-widget={dataWidget}
        ></div>
      </div>
      {!isWidgetLoaded && (
        <Typography.Caption>
          Loading code widget...
          <Spinner size="md" />
        </Typography.Caption>
      )}
    </div>
  );
}

export default CodeFieldSectionSimple;
